import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MultiSelect, Select, Switch, TagsInput, Text, Textarea, TextInput } from '@mantine/core';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { z } from 'zod';
import {
  CATALOG_SOURCES,
  EEAT_RISKS,
  GAME_TYPES,
  LICENSE_TYPES,
  PAYMENT_METHOD_TYPES,
  RG_ORG_TYPES,
  type CatalogSource,
  type EeatRisk,
  type GameType,
  type LicenseType,
  type PaymentMethodType,
  type RgOrgType,
} from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import { RowEditor } from '../components/RowEditor';
import { MediaFileField } from '../components/MediaFileField';
import classes from './EntityPage.module.css';

/**
 * Поля довідників — одне визначення на кожен із чотирьох, а не два.
 *
 * Мокап (`REF_FORM`, `SEO CRM v3.dc.html`) малює для платіжки 12 полів; форма створення мала
 * три, а картка редагування — теж три, але не ті самі (§ звіт 15.08). Причина була
 * структурна: `ReferencePage.tsx` будувала форму створення сама, а цей файл — картку
 * редагування сама, з незалежними наборами полів. Додати поле означало пам'ятати про два
 * місця, і рівно так вони розійшлися.
 *
 * Тепер тут — єдине джерело для кожного з чотирьох довідників: zod-схема форми, дефолти для
 * створення, перетворення значень форми в payload API (спільне для `create` і `patch`) і
 * компонент полів (`XFields`), який `ReferencePage.tsx` монтує в панель створення, а
 * `PayEditor`/`ProviderEditor`/`LicenseEditor`/`RgEditor` нижче — у картку редагування. Поле,
 * додане один раз, з'являється в обох формах за конструкцією, а не за пам'яттю.
 *
 * Ассети (SVG/PNG лого, бейджі) не ввійшли: під них немає завантажувального пайплайна ніде в
 * застосунку (немає ні медіа-роутера, ні UI завантаження) — колонки-посилання на `media_files`
 * є, а шляху заповнити їх немає. Це названо окремо в звіті команді, а не мовчки пропущено.
 */

export const TYPE_LABELS: Record<string, string> = {
  card: 'картка',
  local: 'локальний',
  ewallet: 'e-wallet',
  voucher: 'ваучер',
  crypto: 'крипта',
};

const LICENSE_TYPE_OPTIONS = LICENSE_TYPES.map((t) => ({
  value: t,
  label: t === 'local' ? 'локальна' : 'офшорна',
}));

const RECOGNIZED_GEO_SUGGESTIONS = ['INT', 'LATAM', 'CIS', 'EU'];
const SITE_REQUIREMENT_SUGGESTIONS = ['вік 18+', 'RG-блок', 'T&C', 'KYC до виводу'];

export interface GeoOption {
  id: string;
  countryCode: string;
  countryName: string;
}

/**
 * Стан лінивого запиту для мультиселекту зв'язку (ГЕО методу, ліцензії провайдера).
 *
 * Один і той самий запит з боку створення й з боку редагування — картка й панель фетчать
 * незалежно, react-query дедуплікує за ключем сам.
 */
export interface RelationOptionsState {
  options: { value: string; label: string }[];
  pending: boolean;
  error: unknown;
}

function relationPlaceholder(state: RelationOptionsState, emptyLabel: string): string | undefined {
  if (state.pending) return 'завантаження…';
  if (state.error != null && state.options.length === 0) return 'список недоступний';
  return state.options.length === 0 ? emptyLabel : undefined;
}

/** Порожній рядок означає «прибрати значення», а не «лишити як було» — і на створенні теж. */
function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function nullableNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * `?? ''`, не `=== null ? '' : String(...)`: `String(undefined)` дає рядок `"undefined"`,
 * який тихо валить regex-валідатор і зупиняє сабміт без жодної помилки на екрані — форма
 * просто нічого не робить. Рядки з тесту, заведені до цих полів, довели це на практиці.
 */
function numberToFormString(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Числове текстове поле, а не `NumberInput`: `NumberInput` віддає `number | ''`, а не рядок, і
 * regex-валідатор під ним впав би на числі. Той самий вибір, що вже стояв у формі створення —
 * картка редагування раніше мала тут `NumberInput` без цієї перевірки взагалі.
 */
const optionalNumberField = (min: number, max: number, message: string) =>
  z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    return /^\d+$/.test(value) && Number(value) >= min && Number(value) <= max;
  }, message);

const optionalDecimalField = (min: number, max: number, message: string) =>
  z.string().refine((raw) => {
    const value = raw.trim();
    if (value === '') return true;
    return /^\d+(\.\d{1,2})?$/.test(value) && Number(value) >= min && Number(value) <= max;
  }, message);

// ── платіжні методи ────────────────────────────────────────────────────────────────

export const paymentMethodSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву методу').max(200, 'Максимум 200 символів'),
  type: z.enum(PAYMENT_METHOD_TYPES),
  currencies: z.string(),
  /** `geo_payment_methods` — М2М; підвантажується окремо, у формі — звичайний масив id. */
  geoIds: z.array(z.string()),
  currencyCode: z.string(),
  minDeposit: optionalNumberField(0, 999_999_999, 'Ціле число ≥ 0'),
  maxDeposit: optionalNumberField(0, 999_999_999, 'Ціле число ≥ 0'),
  minWithdrawal: optionalNumberField(0, 999_999_999, 'Ціле число ≥ 0'),
  maxWithdrawal: optionalNumberField(0, 999_999_999, 'Ціле число ≥ 0'),
  processingTime: z.string().max(120, 'Максимум 120 символів'),
  feePercent: optionalDecimalField(0, 100, 'Комісія — число від 0 до 100, напр. 2.5'),
  withdrawalSupported: z.boolean(),
  bonusExcluded: z.boolean(),
  /**
   * Медіа — ідентифікатор файла, порожній рядок означає «немає».
   *
   * Байти йдуть у `POST /media/upload`, який єдиний їх санітизує, і лише повернутий
   * `mediaFileId` лягає у форму. Тому один логотип може стояти в кількох полях, не
   * дублюючись у сховищі, і скасування форми не лишає прикріпленого файла без запису.
   */
  /**
   * Не `.min(1)`: порожній медіа-слот — робочий стан форми (як і `svgDarkId`/`pngIconId`), а
   * не помилка. Обов'язковість `svg_light` — правило `create`/`update` на сервері (decision 1,
   * §15 «Прийомка»), не форми: сервер відхиляє з `VALIDATION_FAILED`, форма показує цю
   * відповідь як звичайну помилку мутації.
   */
  svgLightId: z.string(),
  svgDarkId: z.string(),
  pngIconId: z.string(),
});
export type PaymentMethodFormValues = z.infer<typeof paymentMethodSchema>;

export const paymentMethodInitialValues: PaymentMethodFormValues = {
  name: '',
  type: 'card',
  currencies: '',
  geoIds: [],
  currencyCode: '',
  minDeposit: '',
  maxDeposit: '',
  minWithdrawal: '',
  maxWithdrawal: '',
  processingTime: '',
  feePercent: '',
  withdrawalSupported: true,
  bonusExcluded: false,
  svgLightId: '',
  svgDarkId: '',
  pngIconId: '',
};

/** Спільний payload і для `create`, і для `patch` — те, чого не існувало до сьогодні. */
export function paymentMethodValuesToInput(values: PaymentMethodFormValues): {
  name: string;
  type: PaymentMethodType;
  currencies: string[];
  geoIds: string[];
  currencyCode: string | null;
  minDeposit: number | null;
  maxDeposit: number | null;
  minWithdrawal: number | null;
  maxWithdrawal: number | null;
  processingTime: string | null;
  feeBps: number | null;
  withdrawalSupported: boolean;
  bonusExcluded: boolean;
  svgLightId: string | null;
  svgDarkId: string | null;
  pngIconId: string | null;
} {
  const currencyCode = nullableText(values.currencyCode);
  return {
    name: values.name,
    type: values.type,
    currencies: values.currencies
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean),
    geoIds: values.geoIds,
    currencyCode: currencyCode === null ? null : currencyCode.toUpperCase(),
    minDeposit: nullableNumber(values.minDeposit),
    maxDeposit: nullableNumber(values.maxDeposit),
    minWithdrawal: nullableNumber(values.minWithdrawal),
    maxWithdrawal: nullableNumber(values.maxWithdrawal),
    processingTime: nullableText(values.processingTime),
    feeBps: values.feePercent.trim() === '' ? null : Math.round(Number(values.feePercent) * 100),
    withdrawalSupported: values.withdrawalSupported,
    bonusExcluded: values.bonusExcluded,
    svgLightId: nullableText(values.svgLightId),
    svgDarkId: nullableText(values.svgDarkId),
    pngIconId: nullableText(values.pngIconId),
  };
}

export function PaymentMethodFields({
  form,
  idPrefix,
  geos,
}: {
  form: UseFormReturnType<PaymentMethodFormValues>;
  idPrefix: string;
  geos: RelationOptionsState;
}): React.JSX.Element {
  return (
    <>
      <Select
        size="xs"
        label="Тип"
        allowDeselect={false}
        data={PAYMENT_METHOD_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }))}
        data-testid={`${idPrefix}-type`}
        style={{ width: 130 }}
        {...form.getInputProps('type')}
      />
      <TextInput
        size="xs"
        label="Валюти"
        placeholder="EUR, PLN"
        data-testid={`${idPrefix}-currencies`}
        style={{ width: 150 }}
        {...form.getInputProps('currencies')}
      />
      <MultiSelect
        size="xs"
        label="ГЕО"
        placeholder={relationPlaceholder(geos, 'ГЕО немає')}
        data={geos.options}
        searchable
        disabled={geos.error != null && geos.options.length === 0}
        data-testid={`${idPrefix}-geo`}
        style={{ minWidth: 200 }}
        {...form.getInputProps('geoIds')}
      />
      {geos.error == null ? null : (
        <Text
          className={classes.danger}
          data-testid={`${idPrefix}-geo-error`}
          role="alert"
          size="xs"
        >
          {publicMessageOf(geos.error, 'Не вдалося завантажити ГЕО.')}
        </Text>
      )}
      <TextInput
        size="xs"
        label="Валюта сум"
        placeholder="EUR"
        data-testid={`${idPrefix}-currency-code`}
        style={{ width: 90 }}
        {...form.getInputProps('currencyCode')}
      />
      <TextInput
        size="xs"
        label="Мін. депозит"
        data-testid={`${idPrefix}-min-deposit`}
        style={{ width: 110 }}
        {...form.getInputProps('minDeposit')}
      />
      <TextInput
        size="xs"
        label="Макс. депозит"
        data-testid={`${idPrefix}-max-deposit`}
        style={{ width: 110 }}
        {...form.getInputProps('maxDeposit')}
      />
      <TextInput
        size="xs"
        label="Мін. виведення"
        data-testid={`${idPrefix}-min-withdrawal`}
        style={{ width: 110 }}
        {...form.getInputProps('minWithdrawal')}
      />
      <TextInput
        size="xs"
        label="Макс. виведення"
        data-testid={`${idPrefix}-max-withdrawal`}
        style={{ width: 110 }}
        {...form.getInputProps('maxWithdrawal')}
      />
      <TextInput
        size="xs"
        label="Час обробки"
        placeholder="0–24 год"
        data-testid={`${idPrefix}-processing-time`}
        style={{ width: 140 }}
        {...form.getInputProps('processingTime')}
      />
      <TextInput
        size="xs"
        label="Комісія, %"
        placeholder="2.5"
        data-testid={`${idPrefix}-fee`}
        style={{ width: 90 }}
        {...form.getInputProps('feePercent')}
      />
      <Switch
        size="xs"
        mt={22}
        label="Доступний для виводу"
        data-testid={`${idPrefix}-withdrawal-supported`}
        {...form.getInputProps('withdrawalSupported', { type: 'checkbox' })}
      />
      <Switch
        size="xs"
        mt={22}
        label="Виключення з бонусу"
        data-testid={`${idPrefix}-bonus-excluded`}
        {...form.getInputProps('bonusExcluded', { type: 'checkbox' })}
      />
      <MediaFileField
        label="Логотип SVG"
        spec="SVG для світлої теми"
        value={form.values.svgLightId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('svgLightId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('svgLightId', '');
        }}
        testId={`${idPrefix}-svglight`}
      />
      <MediaFileField
        label="Логотип SVG (тем.)"
        spec="SVG для темної теми"
        value={form.values.svgDarkId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('svgDarkId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('svgDarkId', '');
        }}
        testId={`${idPrefix}-svgdark`}
      />
      <MediaFileField
        label="Іконка PNG"
        spec="PNG 64×64"
        value={form.values.pngIconId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('pngIconId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('pngIconId', '');
        }}
        testId={`${idPrefix}-pngicon`}
      />
    </>
  );
}

// ── ліцензії ────────────────────────────────────────────────────────────────────────

export const licenseSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву регулятора').max(200, 'Максимум 200 символів'),
  jurisdiction: z.string().trim().min(1, 'Вкажіть юрисдикцію'),
  type: z.enum(LICENSE_TYPES),
  abbrFooter: z.string(),
  eeatRisk: z.enum(EEAT_RISKS),
  registryUrl: z.string(),
  registryCheckStatus: z.string(),
  licenseNumberFormat: z.string(),
  recognizedGeos: z.array(z.string()),
  siteRequirements: z.array(z.string()),
  footerTextTemplate: z.string(),
  badgeSvgId: z.string(),
  badgeMonoId: z.string(),
  png512Id: z.string(),
});
export type LicenseFormValues = z.infer<typeof licenseSchema>;

export const licenseInitialValues: LicenseFormValues = {
  name: '',
  jurisdiction: '',
  type: 'offshore',
  abbrFooter: '',
  eeatRisk: 'medium',
  registryUrl: '',
  registryCheckStatus: '',
  licenseNumberFormat: '',
  recognizedGeos: [],
  siteRequirements: [],
  footerTextTemplate: '',
  badgeSvgId: '',
  badgeMonoId: '',
  png512Id: '',
};

export function licenseValuesToInput(values: LicenseFormValues): {
  name: string;
  jurisdiction: string;
  type: LicenseType;
  abbrFooter: string | null;
  eeatRisk: EeatRisk;
  registryUrl: string | null;
  registryCheckStatus: string | null;
  licenseNumberFormat: string | null;
  recognizedGeos: string[];
  siteRequirements: string[];
  footerTextTemplate: string | null;

  badgeSvgId: string | null;
  badgeMonoId: string | null;
  png512Id: string | null;
} {
  return {
    name: values.name,
    jurisdiction: values.jurisdiction,
    type: values.type,
    abbrFooter: nullableText(values.abbrFooter),
    eeatRisk: values.eeatRisk,
    registryUrl: nullableText(values.registryUrl),
    registryCheckStatus: nullableText(values.registryCheckStatus),
    licenseNumberFormat: nullableText(values.licenseNumberFormat),
    recognizedGeos: values.recognizedGeos,
    siteRequirements: values.siteRequirements,
    footerTextTemplate: nullableText(values.footerTextTemplate),
    badgeSvgId: nullableText(values.badgeSvgId),
    badgeMonoId: nullableText(values.badgeMonoId),
    png512Id: nullableText(values.png512Id),
  };
}

export function LicenseFields({
  form,
  idPrefix,
}: {
  form: UseFormReturnType<LicenseFormValues>;
  idPrefix: string;
}): React.JSX.Element {
  return (
    <>
      <TextInput
        size="xs"
        label="Юрисдикція"
        data-testid={`${idPrefix}-jurisdiction`}
        style={{ width: 150 }}
        {...form.getInputProps('jurisdiction')}
      />
      <Select
        size="xs"
        label="Тип"
        allowDeselect={false}
        data={LICENSE_TYPE_OPTIONS}
        data-testid={`${idPrefix}-type`}
        style={{ width: 130 }}
        {...form.getInputProps('type')}
      />
      <TextInput
        size="xs"
        label="У футері"
        data-testid={`${idPrefix}-abbr`}
        style={{ width: 110 }}
        {...form.getInputProps('abbrFooter')}
      />
      <Select
        size="xs"
        label="E-E-A-T ризик"
        allowDeselect={false}
        data={[...EEAT_RISKS]}
        data-testid={`${idPrefix}-risk`}
        style={{ width: 120 }}
        {...form.getInputProps('eeatRisk')}
      />
      <TextInput
        size="xs"
        label="URL реєстру"
        data-testid={`${idPrefix}-registry-url`}
        style={{ width: 220 }}
        {...form.getInputProps('registryUrl')}
      />
      <TextInput
        size="xs"
        label="Статус перевірки реєстру"
        data-testid={`${idPrefix}-registry-status`}
        style={{ width: 200 }}
        {...form.getInputProps('registryCheckStatus')}
      />
      <TextInput
        size="xs"
        label="Формат номера"
        placeholder="ALSI-XXXXXX-FIX"
        data-testid={`${idPrefix}-number-format`}
        style={{ width: 160 }}
        {...form.getInputProps('licenseNumberFormat')}
      />
      <TagsInput
        size="xs"
        label="ГЕО, де визнається"
        data={RECOGNIZED_GEO_SUGGESTIONS}
        data-testid={`${idPrefix}-recognized-geos`}
        style={{ minWidth: 200 }}
        {...form.getInputProps('recognizedGeos')}
      />
      <TagsInput
        size="xs"
        label="Вимоги до сайту"
        data={SITE_REQUIREMENT_SUGGESTIONS}
        data-testid={`${idPrefix}-site-requirements`}
        style={{ minWidth: 220 }}
        {...form.getInputProps('siteRequirements')}
      />
      <Textarea
        size="xs"
        label="Текст у футері"
        placeholder="Licensed and regulated by {regulator}, licence {license_no}"
        // Не `autosize`: він міряє елемент через ResizeObserver, якого немає в DOM
        // компонентного тесту. Те саме рішення, що в `ParsePanel`/`PromptsPage`.
        rows={2}
        data-testid={`${idPrefix}-footer-text`}
        style={{ minWidth: 320 }}
        {...form.getInputProps('footerTextTemplate')}
      />
      <MediaFileField
        label="Бейдж SVG"
        spec="SVG бейдж регулятора для футера"
        value={form.values.badgeSvgId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('badgeSvgId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('badgeSvgId', '');
        }}
        testId={`${idPrefix}-badgesvg`}
      />
      <MediaFileField
        label="Бейдж моно"
        spec="SVG в один колір"
        value={form.values.badgeMonoId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('badgeMonoId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('badgeMonoId', '');
        }}
        testId={`${idPrefix}-badgemono`}
      />
      <MediaFileField
        label="PNG 512"
        spec="PNG 512×512"
        value={form.values.png512Id}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('png512Id', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('png512Id', '');
        }}
        testId={`${idPrefix}-png512`}
      />
    </>
  );
}

// ── провайдери ігор ────────────────────────────────────────────────────────────────

export const providerSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву провайдера').max(200, 'Максимум 200 символів'),
  foundedYear: optionalNumberField(1900, 2100, 'Рік заснування — число від 1900 до 2100'),
  gameTypes: z.array(z.enum(GAME_TYPES)),
  officialUrl: z.string(),
  catalogSource: z.enum(CATALOG_SOURCES),
  catalogSyncCron: z.string(),
  generateProviderPage: z.boolean(),
  /** `game_provider_licenses` — М2М; підвантажується окремо. */
  licenseIds: z.array(z.string()),
  svgLogoId: z.string(),
  banner640x360Id: z.string(),
  topGamePreviewId: z.string(),
});
export type ProviderFormValues = z.infer<typeof providerSchema>;

export const providerInitialValues: ProviderFormValues = {
  name: '',
  foundedYear: '',
  gameTypes: [],
  officialUrl: '',
  catalogSource: 'manual',
  catalogSyncCron: '',
  generateProviderPage: false,
  licenseIds: [],
  svgLogoId: '',
  banner640x360Id: '',
  topGamePreviewId: '',
};

export function providerValuesToInput(values: ProviderFormValues): {
  name: string;
  foundedYear: number | null;
  gameTypes: GameType[];
  officialUrl: string | null;
  catalogSource: CatalogSource;
  catalogSyncCron: string | null;
  generateProviderPage: boolean;
  licenseIds: string[];

  svgLogoId: string | null;
  banner640x360Id: string | null;
  topGamePreviewId: string | null;
} {
  return {
    name: values.name,
    foundedYear: nullableNumber(values.foundedYear),
    gameTypes: values.gameTypes,
    officialUrl: nullableText(values.officialUrl),
    catalogSource: values.catalogSource,
    catalogSyncCron: nullableText(values.catalogSyncCron),
    generateProviderPage: values.generateProviderPage,
    licenseIds: values.licenseIds,
    svgLogoId: nullableText(values.svgLogoId),
    banner640x360Id: nullableText(values.banner640x360Id),
    topGamePreviewId: nullableText(values.topGamePreviewId),
  };
}

export function ProviderFields({
  form,
  idPrefix,
  licenses,
}: {
  form: UseFormReturnType<ProviderFormValues>;
  idPrefix: string;
  licenses: RelationOptionsState;
}): React.JSX.Element {
  return (
    <>
      <TextInput
        size="xs"
        label="Рік заснування"
        placeholder="рік заснування"
        data-testid={`${idPrefix}-founded-year`}
        style={{ width: 130 }}
        {...form.getInputProps('foundedYear')}
      />
      <MultiSelect
        size="xs"
        label="Ліцензії"
        placeholder={relationPlaceholder(licenses, 'ліцензій немає')}
        data={licenses.options}
        searchable
        disabled={licenses.error != null && licenses.options.length === 0}
        data-testid={`${idPrefix}-licenses`}
        style={{ minWidth: 200 }}
        {...form.getInputProps('licenseIds')}
      />
      {licenses.error == null ? null : (
        <Text
          className={classes.danger}
          data-testid={`${idPrefix}-licenses-error`}
          role="alert"
          size="xs"
        >
          {publicMessageOf(licenses.error, 'Не вдалося завантажити ліцензії.')}
        </Text>
      )}
      <MultiSelect
        size="xs"
        label="Типи ігор"
        placeholder="типи ігор"
        data={GAME_TYPES.map((t) => ({ value: t, label: t }))}
        data-testid={`${idPrefix}-game-types`}
        style={{ minWidth: 180 }}
        {...form.getInputProps('gameTypes')}
      />
      <TextInput
        size="xs"
        label="Офіційний сайт"
        data-testid={`${idPrefix}-official-url`}
        style={{ width: 220 }}
        {...form.getInputProps('officialUrl')}
      />
      <Select
        size="xs"
        label="Джерело каталогу"
        allowDeselect={false}
        data={CATALOG_SOURCES.map((s) => ({ value: s, label: s }))}
        data-testid={`${idPrefix}-catalog-source`}
        style={{ width: 140 }}
        {...form.getInputProps('catalogSource')}
      />
      <TextInput
        size="xs"
        label="Автосинк (cron)"
        placeholder="0 4 * * 1"
        data-testid={`${idPrefix}-catalog-cron`}
        style={{ width: 140 }}
        {...form.getInputProps('catalogSyncCron')}
      />
      <Switch
        size="xs"
        mt={22}
        label="Своя сторінка"
        data-testid={`${idPrefix}-own-page`}
        {...form.getInputProps('generateProviderPage', { type: 'checkbox' })}
      />
      <MediaFileField
        label="Логотип SVG"
        spec="SVG логотип провайдера"
        value={form.values.svgLogoId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('svgLogoId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('svgLogoId', '');
        }}
        testId={`${idPrefix}-svglogo`}
      />
      <MediaFileField
        label="Банер"
        spec="PNG/JPG 640×360"
        value={form.values.banner640x360Id}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('banner640x360Id', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('banner640x360Id', '');
        }}
        testId={`${idPrefix}-banner640x360`}
      />
      <MediaFileField
        label="Прев’ю топ-гри"
        spec="PNG/JPG 16:9"
        value={form.values.topGamePreviewId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('topGamePreviewId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('topGamePreviewId', '');
        }}
        testId={`${idPrefix}-topgamepreview`}
      />
    </>
  );
}

// ── організації відповідальної гри ────────────────────────────────────────────────

export const rgOrgSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву організації').max(200, 'Максимум 200 символів'),
  type: z.enum(RG_ORG_TYPES),
  geoId: z.string(),
  helpline: z.string().max(60, 'Гаряча лінія — до 60 символів'),
  url: z.string(),
  disclaimerOriginal: z.string(),
  minAge: optionalNumberField(0, 99, 'Вік — число від 0 до 99'),
  mandatoryInFooter: z.boolean(),
  addToDictionary: z.boolean(),
  svgId: z.string(),
  svgMonoId: z.string(),
  ageIconId: z.string(),
});
export type RgOrgFormValues = z.infer<typeof rgOrgSchema>;

export const rgOrgInitialValues: RgOrgFormValues = {
  name: '',
  type: 'support',
  geoId: '',
  helpline: '',
  url: '',
  disclaimerOriginal: '',
  minAge: '',
  mandatoryInFooter: false,
  addToDictionary: false,
  svgId: '',
  svgMonoId: '',
  ageIconId: '',
};

export function rgOrgValuesToInput(values: RgOrgFormValues): {
  name: string;
  type: RgOrgType;
  geoId: string | null;
  helpline: string | null;
  url: string | null;
  disclaimerOriginal: string | null;
  minAge: number | null;
  mandatoryInFooter: boolean;
  addToDictionary: boolean;

  svgId: string | null;
  svgMonoId: string | null;
  ageIconId: string | null;
} {
  return {
    name: values.name,
    type: values.type,
    geoId: values.geoId.trim() === '' ? null : values.geoId,
    helpline: nullableText(values.helpline),
    url: nullableText(values.url),
    disclaimerOriginal: nullableText(values.disclaimerOriginal),
    minAge: nullableNumber(values.minAge),
    mandatoryInFooter: values.mandatoryInFooter,
    addToDictionary: values.addToDictionary,
    svgId: nullableText(values.svgId),
    svgMonoId: nullableText(values.svgMonoId),
    ageIconId: nullableText(values.ageIconId),
  };
}

export function RgOrgFields({
  form,
  idPrefix,
  geos,
}: {
  form: UseFormReturnType<RgOrgFormValues>;
  idPrefix: string;
  geos: RelationOptionsState;
}): React.JSX.Element {
  return (
    <>
      <Select
        size="xs"
        label="Тип"
        allowDeselect={false}
        data={[...RG_ORG_TYPES]}
        data-testid={`${idPrefix}-type`}
        style={{ width: 150 }}
        {...form.getInputProps('type')}
      />
      <Select
        size="xs"
        label="ГЕО"
        placeholder={relationPlaceholder(geos, 'ГЕО (необовʼязково)')}
        data={geos.options}
        clearable
        disabled={geos.error != null && geos.options.length === 0}
        data-testid={`${idPrefix}-geo`}
        style={{ width: 200 }}
        {...form.getInputProps('geoId')}
      />
      {geos.error == null ? null : (
        <Text
          className={classes.danger}
          data-testid={`${idPrefix}-geo-error`}
          role="alert"
          size="xs"
        >
          {publicMessageOf(geos.error, 'Не вдалося завантажити ГЕО.')}
        </Text>
      )}
      <TextInput
        size="xs"
        label="Гаряча лінія"
        placeholder="гаряча лінія"
        data-testid={`${idPrefix}-helpline`}
        style={{ width: 150 }}
        {...form.getInputProps('helpline')}
      />
      <TextInput
        size="xs"
        label="URL"
        data-testid={`${idPrefix}-url`}
        style={{ width: 200 }}
        {...form.getInputProps('url')}
      />
      <TextInput
        size="xs"
        label="Вік"
        placeholder="вік"
        data-testid={`${idPrefix}-minage`}
        style={{ width: 90 }}
        {...form.getInputProps('minAge')}
      />
      <Textarea
        size="xs"
        label="Дисклеймер (оригінал)"
        // Не `autosize` — та сама причина, що в полі «Текст у футері» ліцензії.
        rows={2}
        data-testid={`${idPrefix}-disclaimer`}
        style={{ minWidth: 260 }}
        {...form.getInputProps('disclaimerOriginal')}
      />
      <Switch
        size="xs"
        mt={22}
        label="Обовʼязково у футері"
        data-testid={`${idPrefix}-footer`}
        {...form.getInputProps('mandatoryInFooter', { type: 'checkbox' })}
      />
      <Switch
        size="xs"
        mt={22}
        label="Додати у словник"
        data-testid={`${idPrefix}-add-dict`}
        {...form.getInputProps('addToDictionary', { type: 'checkbox' })}
      />
      <MediaFileField
        label="Логотип SVG"
        spec="SVG логотип організації"
        value={form.values.svgId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('svgId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('svgId', '');
        }}
        testId={`${idPrefix}-svg`}
      />
      <MediaFileField
        label="Логотип моно"
        spec="SVG в один колір для футера"
        value={form.values.svgMonoId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('svgMonoId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('svgMonoId', '');
        }}
        testId={`${idPrefix}-svgmono`}
      />
      <MediaFileField
        label="Іконка віку"
        spec="SVG/PNG значок 18+/21+"
        value={form.values.ageIconId}
        onUploaded={(mediaFileId) => {
          form.setFieldValue('ageIconId', mediaFileId);
        }}
        onCleared={() => {
          form.setFieldValue('ageIconId', '');
        }}
        testId={`${idPrefix}-ageicon`}
      />
    </>
  );
}

// ── картка редагування ─────────────────────────────────────────────────────────────

export interface EditablePaymentMethod {
  kind: 'pay';
  id: string;
  name: string;
  type: string;
  currencies: string[];
  currencyCode: string | null;
  minDeposit: number | null;
  maxDeposit: number | null;
  minWithdrawal: number | null;
  maxWithdrawal: number | null;
  feeBps: number | null;
  processingTime: string | null;
  withdrawalSupported: boolean;
  bonusExcluded: boolean;
  /** Медіа — ідентифікатори файлів; `null` означає «слот порожній», а не помилку. */
  svgLightId?: string | null;
  svgDarkId?: string | null;
  pngIconId?: string | null;
}

export interface EditableProvider {
  kind: 'provider';
  id: string;
  name: string;
  foundedYear: number | null;
  gameTypes: string[];
  generateProviderPage: boolean;
  officialUrl: string | null;
  catalogSource: string;
  catalogSyncCron: string | null;
  svgLogoId?: string | null;
  banner640x360Id?: string | null;
  topGamePreviewId?: string | null;
}

export interface EditableLicense {
  kind: 'license';
  id: string;
  name: string;
  jurisdiction: string;
  type: string;
  abbrFooter: string | null;
  eeatRisk: string;
  registryUrl: string | null;
  registryCheckStatus: string | null;
  licenseNumberFormat: string | null;
  recognizedGeos: string[];
  siteRequirements: string[];
  footerTextTemplate: string | null;
  badgeSvgId?: string | null;
  badgeMonoId?: string | null;
  png512Id?: string | null;
}

export interface EditableRgOrg {
  kind: 'rg';
  id: string;
  name: string;
  type: string;
  helpline: string | null;
  minAge: number | null;
  mandatoryInFooter: boolean;
  geoId: string | null;
  url: string | null;
  disclaimerOriginal: string | null;
  addToDictionary: boolean;
  svgId?: string | null;
  svgMonoId?: string | null;
  ageIconId?: string | null;
}

export type EditableReferenceRow =
  EditablePaymentMethod | EditableProvider | EditableLicense | EditableRgOrg;

/**
 * Картка рядка довідника — те, чого в реєстрах не існувало.
 *
 * Усі чотири довідники були write-once: `create` був з першого дня, `update` і `delete` не
 * існували навіть в API. Бекенд отримав їх 14.08; цей файл — екран до них, один на всі
 * чотири, бо чотири копії однієї форми розійшлися б на першій же зміні поля.
 *
 * Форма ініціалізується рядком і **пересоздається за `key={row.id}`** на боці сторінки:
 * `useForm` бере `initialValues` один раз, тож без цього клік по другому рядку показував би
 * значення першого — з підписаним іменем другого.
 */
export function ReferenceRowEditor({
  row,
  onClose,
}: {
  row: EditableReferenceRow;
  onClose: () => void;
}): React.JSX.Element {
  switch (row.kind) {
    case 'pay':
      return <PayEditor row={row} onClose={onClose} />;
    case 'provider':
      return <ProviderEditor row={row} onClose={onClose} />;
    case 'license':
      return <LicenseEditor row={row} onClose={onClose} />;
    case 'rg':
      return <RgEditor row={row} onClose={onClose} />;
  }
}

/** Спільна оправа: заголовок, поле «Назва», кнопки, помилки. Не знає про `row.kind`. */
function Shell<T extends { name: string }>({
  title,
  testId,
  form,
  onClose,
  onSubmit,
  busy,
  error,
  onDelete,
  deleting,
  deleteError,
  confirming,
  setConfirming,
  children,
}: {
  title: string;
  testId: string;
  form: UseFormReturnType<T>;
  onClose: () => void;
  onSubmit: (event: React.SyntheticEvent<HTMLFormElement>) => void;
  busy: boolean;
  error: unknown;
  onDelete: () => void;
  deleting: boolean;
  deleteError: unknown;
  confirming: boolean;
  setConfirming: (value: boolean) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <RowEditor
      title={title}
      testId={testId}
      zone="geo_dictionaries"
      onClose={onClose}
      busy={busy}
      error={error}
      deleting={deleting}
      deleteError={deleteError}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      onDelete={onDelete}
      onSubmit={onSubmit}
    >
      <TextInput
        size="xs"
        label="Назва"
        data-testid={`edit-${testId}-name`}
        style={{ width: 190 }}
        {...form.getInputProps('name')}
      />
      {children}
    </RowEditor>
  );
}

/**
 * Спільний хук для обох сторін — картки редагування тут же нижче і панелі створення в
 * `ReferencePage.tsx`. `enabled` — той самий лінивий гейт, що вже стояв на ГЕО для RG: список
 * не тягнеться, поки відповідна панель не відкрита.
 */
export function useGeoOptions(enabled = true): RelationOptionsState {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled,
  });
  const options = ((query.data?.items as GeoOption[] | undefined) ?? []).map((g) => ({
    value: g.id,
    label: `${g.countryCode} — ${g.countryName}`,
  }));
  return { options, pending: query.isPending, error: query.error };
}

export function useLicenseOptions(enabled = true): RelationOptionsState {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.reference.licenses.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled,
  });
  const options = ((query.data?.items as { id: string; name: string }[] | undefined) ?? []).map(
    (l) => ({
      value: l.id,
      label: l.name,
    }),
  );
  return { options, pending: query.isPending, error: query.error };
}

function paymentMethodInitialValuesFromRow(row: EditablePaymentMethod): PaymentMethodFormValues {
  return {
    name: row.name,
    // Значення завжди прийшло з того самого enum на сервері — тип форми лише називає це вголос.
    type: row.type as PaymentMethodType,
    currencies: row.currencies.join(', '),
    geoIds: [],
    currencyCode: row.currencyCode ?? '',
    minDeposit: numberToFormString(row.minDeposit),
    maxDeposit: numberToFormString(row.maxDeposit),
    minWithdrawal: numberToFormString(row.minWithdrawal),
    maxWithdrawal: numberToFormString(row.maxWithdrawal),
    processingTime: row.processingTime ?? '',
    feePercent: row.feeBps === null || row.feeBps === undefined ? '' : String(row.feeBps / 100),
    withdrawalSupported: row.withdrawalSupported,
    bonusExcluded: row.bonusExcluded,
    svgLightId: row.svgLightId ?? '',
    svgDarkId: row.svgDarkId ?? '',
    pngIconId: row.pngIconId ?? '',
  };
}

function PayEditor({
  row,
  onClose,
}: {
  row: EditablePaymentMethod;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const form = useForm<PaymentMethodFormValues>({
    initialValues: paymentMethodInitialValuesFromRow(row),
    validate: zodResolver(paymentMethodSchema),
  });

  const geos = useGeoOptions();
  // ГЕО методу — лінивий запит: `listPaymentMethods` рахує лише `geoCount` заради §4.10, і
  // повний масив id для однієї картки редагування — не те, за що має платити весь список.
  const geoIdsQuery = useQuery(trpc.reference.paymentMethods.geoIds.queryOptions({ id: row.id }));
  useEffect(() => {
    if (geoIdsQuery.data) form.setFieldValue('geoIds', geoIdsQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoIdsQuery.data]);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.reference.paymentMethods.pathKey() });
  };
  const update = useMutation(
    trpc.reference.paymentMethods.update.mutationOptions({ onSuccess: refresh }),
  );
  const remove = useMutation(
    trpc.reference.paymentMethods.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  return (
    <Shell
      title={row.name}
      testId="payment-method"
      form={form}
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirming={confirming}
      setConfirming={setConfirming}
      onSubmit={form.onSubmit((values) => {
        update.mutate({ id: row.id, patch: paymentMethodValuesToInput(values) });
      })}
    >
      <PaymentMethodFields form={form} idPrefix="edit-payment-method" geos={geos} />
    </Shell>
  );
}

function providerInitialValuesFromRow(row: EditableProvider): ProviderFormValues {
  return {
    name: row.name,
    foundedYear: numberToFormString(row.foundedYear),
    gameTypes: row.gameTypes as GameType[],
    officialUrl: row.officialUrl ?? '',
    catalogSource: row.catalogSource as CatalogSource,
    catalogSyncCron: row.catalogSyncCron ?? '',
    generateProviderPage: row.generateProviderPage,
    licenseIds: [],
    svgLogoId: row.svgLogoId ?? '',
    banner640x360Id: row.banner640x360Id ?? '',
    topGamePreviewId: row.topGamePreviewId ?? '',
  };
}

function ProviderEditor({
  row,
  onClose,
}: {
  row: EditableProvider;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const form = useForm<ProviderFormValues>({
    initialValues: providerInitialValuesFromRow(row),
    validate: zodResolver(providerSchema),
  });

  const licenses = useLicenseOptions();
  const licenseIdsQuery = useQuery(
    trpc.reference.providers.licenseIds.queryOptions({ id: row.id }),
  );
  useEffect(() => {
    if (licenseIdsQuery.data) form.setFieldValue('licenseIds', licenseIdsQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseIdsQuery.data]);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.reference.providers.pathKey() });
  };
  const update = useMutation(
    trpc.reference.providers.update.mutationOptions({ onSuccess: refresh }),
  );
  const remove = useMutation(
    trpc.reference.providers.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  return (
    <Shell
      title={row.name}
      testId="provider"
      form={form}
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirming={confirming}
      setConfirming={setConfirming}
      onSubmit={form.onSubmit((values) => {
        update.mutate({ id: row.id, patch: providerValuesToInput(values) });
      })}
    >
      <ProviderFields form={form} idPrefix="edit-provider" licenses={licenses} />
    </Shell>
  );
}

function licenseInitialValuesFromRow(row: EditableLicense): LicenseFormValues {
  return {
    name: row.name,
    jurisdiction: row.jurisdiction,
    type: row.type as LicenseType,
    abbrFooter: row.abbrFooter ?? '',
    eeatRisk: row.eeatRisk as EeatRisk,
    registryUrl: row.registryUrl ?? '',
    registryCheckStatus: row.registryCheckStatus ?? '',
    licenseNumberFormat: row.licenseNumberFormat ?? '',
    recognizedGeos: row.recognizedGeos,
    siteRequirements: row.siteRequirements,
    footerTextTemplate: row.footerTextTemplate ?? '',
    badgeSvgId: row.badgeSvgId ?? '',
    badgeMonoId: row.badgeMonoId ?? '',
    png512Id: row.png512Id ?? '',
  };
}

function LicenseEditor({
  row,
  onClose,
}: {
  row: EditableLicense;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const form = useForm<LicenseFormValues>({
    initialValues: licenseInitialValuesFromRow(row),
    validate: zodResolver(licenseSchema),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.reference.licenses.pathKey() });
  };
  const update = useMutation(
    trpc.reference.licenses.update.mutationOptions({ onSuccess: refresh }),
  );
  const remove = useMutation(
    trpc.reference.licenses.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  return (
    <Shell
      title={row.name}
      testId="license"
      form={form}
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirming={confirming}
      setConfirming={setConfirming}
      onSubmit={form.onSubmit((values) => {
        update.mutate({ id: row.id, patch: licenseValuesToInput(values) });
      })}
    >
      <LicenseFields form={form} idPrefix="edit-license" />
    </Shell>
  );
}

function rgOrgInitialValuesFromRow(row: EditableRgOrg): RgOrgFormValues {
  return {
    name: row.name,
    type: row.type as RgOrgType,
    geoId: row.geoId ?? '',
    helpline: row.helpline ?? '',
    url: row.url ?? '',
    disclaimerOriginal: row.disclaimerOriginal ?? '',
    minAge: numberToFormString(row.minAge),
    mandatoryInFooter: row.mandatoryInFooter,
    addToDictionary: row.addToDictionary,
    svgId: row.svgId ?? '',
    svgMonoId: row.svgMonoId ?? '',
    ageIconId: row.ageIconId ?? '',
  };
}

function RgEditor({
  row,
  onClose,
}: {
  row: EditableRgOrg;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const form = useForm<RgOrgFormValues>({
    initialValues: rgOrgInitialValuesFromRow(row),
    validate: zodResolver(rgOrgSchema),
  });

  const geos = useGeoOptions();

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.reference.rgOrganizations.pathKey() });
  };
  const update = useMutation(
    trpc.reference.rgOrganizations.update.mutationOptions({ onSuccess: refresh }),
  );
  const remove = useMutation(
    trpc.reference.rgOrganizations.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  return (
    <Shell
      title={row.name}
      testId="rg-org"
      form={form}
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirming={confirming}
      setConfirming={setConfirming}
      onSubmit={form.onSubmit((values) => {
        update.mutate({ id: row.id, patch: rgOrgValuesToInput(values) });
      })}
    >
      <RgOrgFields form={form} idPrefix="edit-rg-org" geos={geos} />
    </Shell>
  );
}
